// components/modal/GroupProfileModal.tsx
import React, { useState, useEffect, useCallback } from "react";
import API from "../../api";
import toastr from "toastr";

// ─── Types ─────────────────────────────────────────────────────────────────
interface TestOption {
  id: number;
  category_name: string;
  tests: { id: number; test_name: string; price: number; test_category: number }[];
}

interface LineItem {
  item_id: number;
  item_category: number;
  item_name: string;
  item_amount: number;
}

interface GroupProfile {
  id: number;
  category_name: string;
  description?: string;
  notes?: string;
  amount?: number;
  items?: LineItem[];
}

interface Props {
  show: boolean;
  onClose: () => void;
  mode: "add" | "edit";
  profileData?: GroupProfile | null;
  onSuccess: () => void;
}

const emptySelected = {
  item_id: 0,
  item_category: 0,
  item_name: "",
  item_amount: 0,
};

// ─── Component ─────────────────────────────────────────────────────────────
const GroupProfileModal: React.FC<Props> = ({
  show,
  onClose,
  mode,
  profileData,
  onSuccess,
}) => {
  const [testOptions, setTestOptions] = useState<TestOption[]>([]);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [selected, setSelected] = useState({ ...emptySelected });
  const [profileName, setProfileName] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // ── Load dropdown data once ─────────────────────────────────────────────
  useEffect(() => {
    if (!show) return;
    setLoadingOptions(true);
    API.get("groupProfiles/getTestDropdownData")
      .then((res) => setTestOptions(res.data.data || []))
      .catch(() => toastr.error("Failed to load test options"))
      .finally(() => setLoadingOptions(false));
  }, [show]);

  // ── Pre-fill when editing ───────────────────────────────────────────────
  useEffect(() => {
    if (!show) return;
    if (mode === "edit" && profileData) {
      setProfileName(profileData.category_name || "");
      setNotes(profileData.notes || "");
      setLineItems(
        (profileData.items || []).map((it: any) => ({
          item_id: Number(it.item_id) || 0,
          item_category: Number(it.test_category) || Number(it.item_category) || 0,
          item_name: it.item_name || "",
          item_amount: Number(it.amount) || Number(it.item_amount) || 0,
        }))
      );
    } else {
      setProfileName("");
      setNotes("");
      setLineItems([]);
    }
    setSelected({ ...emptySelected });
  }, [show, mode, profileData]);

  // ── Total amount ────────────────────────────────────────────────────────
  const totalAmount = lineItems.reduce((sum, it) => sum + (Number(it.item_amount) || 0), 0);

  // ── Dropdown change: resolve test or category ───────────────────────────
  const handleDropdownChange = useCallback(
    async (val: string) => {
      if (!val || val === "placeholder") {
        setSelected({ ...emptySelected });
        return;
      }

      if (val.startsWith("CategoryID_")) {
        const catId = parseInt(val.replace("CategoryID_", ""), 10);
        const cat = testOptions.find((c) => c.id === catId);
        setSelected({
          item_id: catId,
          item_category: catId,
          item_name: cat?.category_name || "",
          item_amount: 0,
        });
      } else if (val.startsWith("test_id_")) {
        const testId = parseInt(val.replace("test_id_", ""), 10);
        let found: { id: number; test_name: string; price: number; test_category: number } | undefined;
        let catId = 0;
        for (const cat of testOptions) {
          found = cat.tests.find((t) => t.id === testId);
          if (found) { catId = cat.id; break; }
        }
        if (found) {
          const price = Number(found.price) || 0;
          setSelected({
            item_id: found.id,
            item_category: catId,
            item_name: found.test_name,
            item_amount: price,
          });
        }
      }
    },
    [testOptions]
  );

  // ── Add row ─────────────────────────────────────────────────────────────
  const handleAddRow = useCallback(async () => {
    if (!selected.item_name) {
      toastr.warning("Please select a test first");
      return;
    }

    // Duplicate check
    if (lineItems.some((it) => it.item_id === selected.item_id)) {
      toastr.warning(`"${selected.item_name}" is already added`);
      return;
    }

    // If a whole Category is selected, expand it into its individual tests
    if (selected.item_amount === 0 && selected.item_id === selected.item_category) {
      try {
        const res = await API.get(`groupProfiles/getTestCategoryItems/${selected.item_id}`);
        const tests: { id: number; test_name: string; price: number; test_category: number }[] =
          res.data.data || [];
        const newRows: LineItem[] = tests.map((t) => ({
          item_id: t.id,
          item_category: t.test_category,
          item_name: t.test_name,
          item_amount: Number(t.price) || 0,
        }));
        setLineItems((prev) => [...prev, ...newRows.filter(
          (nr) => !prev.some((p) => p.item_id === nr.item_id)
        )]);
      } catch {
        toastr.error("Failed to load category tests");
      }
      return;
    }

    setLineItems((prev) => [...prev, { ...selected }]);
    setSelected({ ...emptySelected });
  }, [selected, lineItems]);

  // ── Delete row ──────────────────────────────────────────────────────────
  const handleDeleteRow = useCallback((index: number) => {
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // ── Save ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!profileName.trim()) {
      toastr.error("Profile name is required");
      return;
    }
    setSaving(true);
    try {
      // Match PHP payload structure: item_id, item_name, item_category, item_amount
      const payload = {
        category_name: profileName.trim(),
        description: notes,
        notes,
        amount: totalAmount,
        items: lineItems.map(it => ({
          item_id: it.item_id,
          item_name: it.item_name,
          item_category: it.item_category,
          item_amount: it.item_amount,
        })),
      };
      if (mode === "add") {
        await API.post("groupProfiles/createGroupProfile", payload);
        toastr.success("Group profile created successfully");
      } else {
        await API.put(`groupProfiles/updateGroupProfile/${profileData!.id}`, payload);
        toastr.success("Group profile updated successfully");
      }
      onSuccess();
    } catch (err: any) {
      toastr.error(err.response?.data?.message || "Failed to save group profile");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => { if (!saving) onClose(); };

  if (!show) return null;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div
      className="modal show d-block"
      style={{ background: "rgba(0,0,0,0.65)", zIndex: 1050 }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className="modal-dialog modal-xl"
        style={{ maxWidth: "900px" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content bg-dark text-white">
          {/* Header */}
          <div className="modal-header border-secondary">
            <h5 className="modal-title">
              {mode === "add" ? "Create New Profile" : "Edit Profile"}
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleClose}
              disabled={saving}
            />
          </div>

          {/* Body */}
          <div className="modal-body">
            {/* ── Test selector row ───────────────────────────── */}
            <div className="row mb-3">
              <div className="col-md-9">
                <label className="form-label text-muted small">Select Test / Category</label>
                <select
                  className="form-select bg-dark text-white border-secondary"
                  onChange={(e) => handleDropdownChange(e.target.value)}
                  value={
                    selected.item_id
                      ? selected.item_amount === 0 && selected.item_id === selected.item_category
                        ? `CategoryID_${selected.item_id}`
                        : `test_id_${selected.item_id}`
                      : "placeholder"
                  }
                  disabled={loadingOptions}
                >
                  <option value="placeholder">
                    {loadingOptions ? "Loading..." : "-- Select Test --"}
                  </option>
                  {testOptions.map((cat) => (
                    <React.Fragment key={cat.id}>
                      <option value={`CategoryID_${cat.id}`}>
                        {cat.category_name}
                      </option>
                      {cat.tests.map((t) => (
                        <option key={t.id} value={`test_id_${t.id}`}>
                          {"\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0"}{t.test_name}
                        </option>
                      ))}
                    </React.Fragment>
                  ))}
                </select>
              </div>

              <div className="col-md-3 d-flex align-items-end">
                <div className="w-100">
                  <label className="form-label text-muted small">Amount</label>
                  <div className="input-group">
                    <span className="input-group-text bg-secondary text-white border-secondary">₹</span>
                    <input
                      readOnly
                      type="text"
                      className="form-control bg-dark text-white border-secondary text-end"
                      value={selected.item_amount || ""}
                      placeholder="0"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Description (read-only, filled from selection) */}
            <div className="row mb-3">
              <div className="col-md-9">
                <label className="form-label text-muted small">Description</label>
                <input
                  readOnly
                  type="text"
                  className="form-control bg-dark text-white border-secondary"
                  value={selected.item_name}
                  placeholder="Auto-filled on selection"
                />
              </div>
              <div className="col-md-3 d-flex align-items-end">
                <button
                  className="btn btn-success w-100"
                  onClick={handleAddRow}
                  disabled={!selected.item_name || saving}
                >
                  <i className="fa fa-plus me-1" /> Add
                </button>
              </div>
            </div>

            {/* ── Line items table ─────────────────────────────── */}
            <div className="table-responsive mb-3">
              <table className="table table-bordered table-sm mb-0" id="tab_logic">
                <thead style={{ backgroundColor: "#81888c", color: "#fff" }}>
                  <tr>
                    <th style={{ width: 40 }} className="text-center">S.No</th>
                    <th>Description</th>
                    <th className="text-end" style={{ width: 110 }}>Amount</th>
                    <th style={{ width: 60 }} className="text-center">Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center text-muted py-3">
                        No items added yet
                      </td>
                    </tr>
                  ) : (
                    lineItems.map((it, idx) => (
                      <tr key={idx}>
                        <td className="text-center">{idx + 1}</td>
                        <td>{it.item_name}</td>
                        <td className="text-end">₹{Number(it.item_amount || 0).toFixed(2)}</td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteRow(idx)}
                            title="Remove"
                            disabled={saving}
                          >
                            <i className="fa fa-trash" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* ── Profile name + total ─────────────────────────── */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">
                  Profile Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control bg-dark text-white border-secondary"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  placeholder="Enter profile name"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Total Amount</label>
                <div className="input-group">
                  <span className="input-group-text bg-secondary text-white border-secondary">₹</span>
                  <input
                    readOnly
                    type="text"
                    className="form-control bg-dark text-white border-secondary text-end fw-bold"
                    value={totalAmount.toFixed(2)}
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="mb-2">
              <label className="form-label">Notes</label>
              <textarea
                className="form-control bg-dark text-white border-secondary"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={100}
                placeholder="Optional notes..."
              />
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer border-secondary">
            <button
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={saving}
            >
              <i className="fa fa-arrow-left me-1" /> Back to List
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="fa fa-check me-1" />
                  {mode === "add" ? "Save" : "Update"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupProfileModal;